<template>
    <div dusk="SmartZoomModal" class="fixed inset-0 z-40 inline-flex h-screen w-screen justify-center">
        <div
            dusk="SmartZoomModalBackdrop"
            aria-hidden="true"
            class="absolute inset-0 bg-black/60"
            @click.prevent="toggleSmartZoom"
        ></div>
        <div
            ref="focusTrapContainer"
            dusk="SmartZoomModalContainer"
            role="dialog"
            aria-modal="true"
            :aria-label="currentArticle?.title"
            class="relative z-50 m-0 grid max-h-full grid-cols-1 rounded bg-theme-bg py-1 shadow-2xl sm:my-2 sm:max-w-[480px] md:max-w-[600px] lg:max-w-[760px]"
            :class="{ 'grid-rows-[50px_auto_45px]': totalArticles > 1, 'grid-rows-[50px_auto]': totalArticles <= 1 }"
        >
            <SmartZoomModalHeader
                ref="modalHeader"
                :current-article="currentArticle"
                :current-article-number="currentArticleNumber"
                :total-articles="totalArticles"
            ></SmartZoomModalHeader>
            <div class="relative h-full overflow-hidden">
                <!-- Scrollable content -->
                <div
                    ref="modalContent"
                    dusk="SmartZoomModalContent"
                    class="h-full overflow-y-auto overflow-x-hidden leading-relaxed text-theme-text transition-[font-size] duration-100 ease-in"
                    :style="style"
                >
                    <div class="container mx-auto px-4 py-8">
                        <div class="flex flex-wrap items-center justify-between md:mt-0">
                            <div v-if="currentArticle" :lang="issue.lang" class="mb-4 mt-5 w-full md:mb-2 md:mt-2.5">
                                <!-- eslint-disable vue/no-v-html -->
                                <p
                                    v-if="currentArticle.pretitle"
                                    class="mb-2 leading-relaxed"
                                    style="font-size: 0.875em"
                                    v-html="currentArticle.pretitle"
                                ></p>
                                <!-- eslint-enable vue/no-v-html -->
                                <h1
                                    class="m-0 text-left font-semibold leading-tight tracking-tight transition-[font-size] duration-100 md:mb-0"
                                    style="font-size: 1.875em"
                                >
                                    {{ currentArticle.title }}
                                </h1>
                                <p v-if="currentArticle.author" class="mt-1 italic" style="font-size: 1em">
                                    {{ localizations[lang]['by'] + ' ' + currentArticle.author }}
                                </p>
                                <p v-if="currentArticle.by_line" class="mb-0 leading-normal" style="font-size: 0.875em">
                                    {{ currentArticle.by_line }}
                                </p>
                            </div>
                            <div v-if="currentArticle?.image_url" class="w-full text-center">
                                <figure class="overflow-hidden rounded">
                                    <img
                                        class="pointer-events-none w-full"
                                        :src="currentArticle.image_url"
                                        :alt="currentArticle.image_caption"
                                    />
                                    <figcaption
                                        v-if="currentArticle.image_caption"
                                        class="bg-black/5 px-5 py-1 text-left font-light"
                                        style="font-size: 0.875em"
                                    >
                                        {{ currentArticle.image_caption }}
                                    </figcaption>
                                </figure>
                            </div>
                        </div>
                        <div v-if="currentArticle" class="flex flex-wrap">
                            <div class="trix-content w-full">
                                <SmartZoomModalContent
                                    :content="currentArticle.body"
                                    :content-language="issue.lang"
                                ></SmartZoomModalContent>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Gradient overlays -->
                <div
                    class="pointer-events-none absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-theme-bg to-transparent"
                ></div>
                <div
                    class="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-theme-bg to-transparent"
                ></div>
            </div>
            <transition name="fade">
                <SmartZoomModalFooter
                    v-if="totalArticles > 1"
                    ref="modalFooter"
                    :is-first-article="isFirstArticle"
                    :is-last-article="isLastArticle"
                    :current-article-number="currentArticleNumber"
                    :total-articles="totalArticles"
                    :on-prev="prevArticle"
                    :on-next="nextArticle"
                ></SmartZoomModalFooter>
            </transition>
        </div>
    </div>
</template>

<script>
'use strict';

import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { mapMutations, mapState } from 'vuex';
import SmartZoomModalHeader from './SmartZoomModalHeader.vue';
import SmartZoomModalContent from './SmartZoomModalContent.vue';
import SmartZoomModalFooter from './SmartZoomModalFooter.vue';
import Hammer from 'hammerjs';
import { uiAPI } from '../../../src/js/communication';
import focusTrapMixin from '../../js/mixins/focusTrapMixin';

export default {
    components: {
        SmartZoomModalHeader,
        SmartZoomModalContent,
        SmartZoomModalFooter,
    },
    mixins: [focusTrapMixin],
    props: {
        articles: {
            type: Array,
            default: () => [],
        },
        sharedArticleIndex: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            currentIndex: 0,
            hammerInstance: null,
            focusTrapEnabled: true,
        };
    },
    computed: {
        ...mapState(['lang', 'smartZoom', 'localizations', 'sharedArticle', 'currentArticleIndex', 'issue']),
        style() {
            return {
                '--zoom-multiplier': this.smartZoom.fontSizeMultiplier,
            };
        },
        currentArticle() {
            return this.articles?.[this.currentIndex] ?? null;
        },
        currentArticleNumber() {
            return this.currentIndex + 1;
        },
        totalArticles() {
            return this.articles?.length ?? 0;
        },
        isFirstArticle() {
            return this.currentIndex === 0;
        },
        isLastArticle() {
            return this.currentArticleNumber === this.totalArticles;
        },
        heightBars() {
            return {
                header: this.$refs.modalHeader.$el.offsetHeight,
                // footer: this.$refs.modalFooter.$el.offsetHeight
            };
        },
    },
    watch: {
        currentIndex() {
            if (this.$refs.modalContent) {
                this.$refs.modalContent.scrollTop = 0;
            }
        },
    },
    mounted() {
        this.listenBackButtonForClosing();

        // Handle shared article index if present
        if (this.sharedArticle.id && !this.sharedArticle.showed && this.articles) {
            const index = this.articles.findIndex(article => article.id === this.sharedArticle.id);
            this.currentIndex = index >= 0 ? index : 0;
            this.$store.commit('setSharedArticle', {
                showed: true,
            });
        }

        // This is to fix a weird iOS Safari scroll issue
        disableBodyScroll(this.$refs.modalContent);

        document.addEventListener('keydown', this.handleKeyboardArrowKeys, false);

        // Ensure DOM is ready before initializing Hammer
        this.$nextTick(() => {
            if (this.$refs.modalContent && !this.hammerInstance) {
                this.hammerInstance = new Hammer.Manager(this.$refs.modalContent);
                this.hammerInstance.add(new Hammer.Swipe({ direction: Hammer.DIRECTION_HORIZONTAL }));
                this.hammerInstance.on('swipeleft', () => {
                    if (!this.isLastArticle) {
                        this.nextArticle();
                    }
                });
                this.hammerInstance.on('swiperight', () => {
                    if (!this.isFirstArticle) {
                        this.prevArticle();
                    }
                });
            }
        });
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.handleKeyboardArrowKeys);

        // Destroy Hammer instance
        if (this.hammerInstance) {
            this.hammerInstance.destroy();
            this.hammerInstance = null;
        }

        // This is to fix a weird iOS Safari scroll issue
        if (this.$refs.modalContent) {
            enableBodyScroll(this.$refs.modalContent);
        }
    },
    methods: {
        ...mapMutations(['toggleSmartZoom', 'setSharedArticle']),
        onFocusTrapEscape() {
            this.toggleSmartZoom();
        },
        handleKeyboardArrowKeys(e) {
            if (e.key === 'ArrowLeft') (e.preventDefault(), this.prevArticle());
            if (e.key === 'ArrowRight') (e.preventDefault(), this.nextArticle());
        },
        prevArticle() {
            if (this.currentIndex > 0) this.currentIndex--;
        },
        nextArticle() {
            const articlesLength = this.articles?.length ?? 0;
            if (this.currentIndex < articlesLength - 1) this.currentIndex++;
        },
        hasSharedArticle() {
            return this.sharedArticle.id && !this.sharedArticle.showed;
        },
        setCurrentArticle() {
            if (this.sharedArticle.id && !this.sharedArticle.showed && this.articles) {
                const index = this.articles.findIndex(article => article.id === this.sharedArticle.id);
                this.currentIndex = index >= 0 ? index : 0;
                this.$store.commit('setSharedArticle', {
                    showed: true,
                });
            }
        },
        listenBackButtonForClosing() {
            const url = new URL(window.location);
            if (url.searchParams.has('smartzoom')) return;

            url.searchParams.set('smartzoom', 'open');
            window.history.pushState({}, '', url);

            window.onpopstate = () => {
                this.smartZoom.visible ? this.toggleSmartZoom() : uiAPI.close();
            };
        },
    },
};
</script>

<style scoped>
/* iOS Safari momentum scrolling */
.overflow-y-auto {
    -webkit-overflow-scrolling: touch;
    overflow-scrolling: touch;
}

/* Dynamic font sizing using CSS custom property */
[style*='--zoom-multiplier'] {
    font-size: calc((18px * var(--zoom-multiplier)) + (21 - 18) * ((100vw - 400px) / (1200 - 400)));
}
</style>
